import { CircleCheck, Info, Loader2, OctagonX, TriangleAlert } from 'lucide-react';
import { useTheme } from 'next-themes';
import type { CSSProperties } from 'react';
import { Toaster as SonnerToaster, type ToasterProps } from 'sonner';

/**
 * Avisos do sistema. Montado uma vez em `Providers`; as telas chamam `toast` de
 * `@/lib/toast`.
 *
 * O tema vem do `next-themes` e não do sistema operacional: quem escolheu claro num
 * ambiente escuro receberia aviso escuro sobre tela clara.
 */
export function Toaster(props: ToasterProps) {
  const { theme = 'system' } = useTheme();

  return (
    <SonnerToaster
      theme={theme as ToasterProps['theme']}
      /*
       * Topo e centro, e não o canto inferior direito que é o padrão do sonner.
       *
       * Quem clicou em "Salvar" no meio da tela olha o meio da tela; num monitor
       * grande, a confirmação aparecia na diagonal oposta, fora do campo de atenção.
       * Vem antes do `...props` de propósito: é o padrão do sistema, e uma chamada
       * específica ainda pode sobrescrever.
       */
      position="top-center"
      /*
       * Recuo abaixo do cabeçalho. No celular ele carrega o botão do menu — a única
       * navegação da tela —, e com o recuo padrão o aviso cobria justamente ele.
       *
       * `mobileOffset` junto porque abaixo de 600px o sonner ignora `offset` e passa a
       * usar as variáveis de mobile: só com o primeiro, o aviso continuava colado no
       * topo exatamente no caso que este recuo existe para resolver.
       */
      offset={{ top: '3.5rem' }}
      mobileOffset={{ top: '3.5rem' }}
      icons={{
        success: <CircleCheck className="size-5" aria-hidden />,
        info: <Info className="size-5" aria-hidden />,
        warning: <TriangleAlert className="size-5" aria-hidden />,
        error: <OctagonX className="size-5" aria-hidden />,
        loading: <Loader2 className="size-5 animate-spin" aria-hidden />,
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
          /*
           * Mais largo que os 356px padrão do sonner. O aviso estava discreto demais para uma
           * confirmação de ação (ver o degradê por tipo em `globals.css`); a largura acompanha o
           * peso maior. Em `rem` porque a escala da interface sai da raiz.
           */
          '--width': '25rem',
        } as CSSProperties
      }
      {...props}
    />
  );
}
